import { useLayoutEffect, useRef } from "react";
import {
    normalizeCogniveId,
    OPTIMIZER_DISPLAY_IDENTITY_FIELDS,
    OPTIMIZER_RISK_PRESENTATION_FIELDS,
    preserveOptimizerDisplayIdentity,
} from './optimizerIdentityHelpers';

const pickPositionGmv = (sel, mode, currentPrice) => {
    if (mode === "pos-sizing") {
        if (!sel?.cognive_id || sel.is_position === false) return 0;
        const quantity = sel.quantity == null ? NaN : Number(sel.quantity);
        if (quantity === 0) return 0;
        const price = currentPrice == null ? NaN : Number(currentPrice);
        return Number.isFinite(quantity) && Number.isFinite(price)
            ? quantity * price
            : null;
    }
    return (
        sel?.nmv ??
        sel?.pos_gmv_dollar ?? 0
    );
};

const buildRowsSignature = (rows, baselineSource) => [
    baselineSource,
    ...rows.map((row) => (
        [
        row.id,
        row.ticker ?? '',
        row.inbound_id_type ?? '',
        row.inbound_id_value ?? '',
        row.price ?? '',
        row.adtv ?? '',
        row.positionGmv ?? '',
        row.posSizingPositionGmv ?? '',
        row.beta ?? '',
        row.exp_return ?? '',
        row.trade_idea ?? '',
        row.baselineMember ? 1 : 0,
        row.checkBoxEnable ? 1 : 0,
        ...OPTIMIZER_DISPLAY_IDENTITY_FIELDS.map((field) => (
            JSON.stringify(row?.[field] ?? null)
        )),
        ...OPTIMIZER_RISK_PRESENTATION_FIELDS.map((field) => (
            JSON.stringify(row?.[field] ?? null)
        )),
        ].join(':')
    )),
].join('|');


export function projectOptimizerBaseRows({ viewMode, industries, combinedHoldings, posSizingData,
    posSizingOptimizerContext, optimizerSelection = null, previousActualMembershipSignature = null,
    optimizerWorkspaceAvailable = true }) {
    // A verified empty Watchlist is a complete empty workspace in either mode,
    // independent of whether a saved Position Sizing context can be run.
    if (optimizerWorkspaceAvailable === false) return {
        ready: true, rows: [], baselineIdentityError: null,
        discardKeptSelection: viewMode === 'normal' && previousActualMembershipSignature != null
            && previousActualMembershipSignature !== '' && optimizerSelection != null,
        actualMembershipSignature: '', finalTickerDataFlag: true,
        rowsSignature: buildRowsSignature([], `live:${viewMode}:${viewMode === 'pos-sizing'
            ? posSizingOptimizerContext?.context_document_sha256 ?? '' : ''}`),
    };
    const posSizingOptimizerContextReady = Boolean(
        posSizingOptimizerContext
        && posSizingOptimizerContext.availability === 'available'
        && posSizingOptimizerContext.runnability === 'runnable'
        && Array.isArray(posSizingOptimizerContext.optimizer_rows)
    );
    if (
        viewMode === 'pos-sizing'
        && !posSizingOptimizerContextReady
    ) {
        return { ready: false };
    }

    const toArray = (value) => {
        if (Array.isArray(value)) return value;
        if (value && typeof value === 'object') return Object.values(value);
        return [];
    };

    const savedOptimizerRows = toArray(posSizingOptimizerContext?.optimizer_rows);
    const savedOptimizerBaselineActive = (
        viewMode === 'pos-sizing'
        && Boolean(posSizingOptimizerContext)
    );
    const selectedArray = savedOptimizerBaselineActive
        ? savedOptimizerRows.filter((row) => row?.is_position === true)
        : viewMode === "pos-sizing"
            ? toArray(posSizingData)
            : toArray(combinedHoldings);
    const unjoinableSelectedCount = selectedArray.filter(
        (row) => !normalizeCogniveId(row?.cognive_id),
    ).length;
    const baselineIdentityError = unjoinableSelectedCount > 0
        ? `${unjoinableSelectedCount} optimizer baseline row${unjoinableSelectedCount === 1 ? '' : 's'} cannot be joined without canonical identity.`
        : null;
    const indexByCogniveId = (arr) => {
        const safeArr = Array.isArray(arr) ? arr : toArray(arr);
        return safeArr.reduce((acc, row) => {
            const cogniveId = normalizeCogniveId(row?.cognive_id);
            if (cogniveId) {
                acc[cogniveId] = row;
            }
            return acc;
        }, {});
    };

    const industriesArray = toArray(industries);

    const industriesBy = indexByCogniveId(industriesArray);
    const selectedBy   = indexByCogniveId(selectedArray);
    const actualBy = indexByCogniveId(combinedHoldings);
    const actualMembershipSignature = Object.keys(actualBy).sort().join('|');
    const actualMembershipChanged = (
        previousActualMembershipSignature != null
        && previousActualMembershipSignature !== actualMembershipSignature
    );
    // A kept Current selection belongs to one Actual membership. Portfolio
    // imports and Watchlist-driven Actual additions/removals discard it, so
    // the new Actual holdings are checked instead.
    const discardKeptSelection = (
        viewMode === 'normal'
        && actualMembershipChanged
        && optimizerSelection != null
    );
    const keptSelection = discardKeptSelection ? null : optimizerSelection;
    const savedDraftBy = indexByCogniveId(posSizingData || []);
    const posSizingBy = savedOptimizerBaselineActive
        ? indexByCogniveId(savedOptimizerRows)
        : savedDraftBy;

    // The current Watchlist is the sole row universe. Baseline data and
    // optimizer selection are independent overlays joined by Cognive ID.
    const selectedBaselineEmpty = selectedArray.length === 0;
    const securityKeys = Object.keys(industriesBy);

    const projectedRows = securityKeys.map((securityKey) => {
        const base = industriesBy[securityKey] || {};
        const sel  = selectedBy[securityKey]   || {};
        const riskSource = viewMode === 'pos-sizing'
            ? (savedDraftBy[securityKey] || {})
            : sel;
        const riskFallback = viewMode === 'pos-sizing' ? {} : base;

        // Watchlist rows define the selectable universe, but never supply
        // portfolio-owned GMV or trade ideas. A security absent from the
        // chosen baseline starts at numeric zero and can still be selected
        // as a new optimization candidate.
        const positionGmv = pickPositionGmv(sel, viewMode, base?.price);
        const posSizingPositionGmv = pickPositionGmv(posSizingBy[securityKey], 'pos-sizing', base?.price);
        const hasBaselineGmv = Number.isFinite(Number(positionGmv))
            && Number(positionGmv) !== 0;
        const expRet = savedOptimizerBaselineActive ? (posSizingBy[securityKey]?.exp_return ?? 0)
            : hasBaselineGmv ? (sel?.exp_return ?? null) : 0;
        const tradeIdea = savedOptimizerBaselineActive ? (posSizingBy[securityKey]?.trade_idea ?? 0)
            : hasBaselineGmv ? (sel?.trade_idea ?? null) : 0;
        // In Current, a Watchlist name outside the holdings has no risk
        // row: its per-security vols come from the Watchlist's factor
        // columns (already in percent) and a zero position carries zero
        // dollar risk.
        const watchlistFallback = viewMode !== 'pos-sizing' && !selectedBy[securityKey];
        const zeroDollarRisk = watchlistFallback && !hasBaselineGmv ? 0 : null;
        const factorRiskDollar = (
            riskSource?.factorRiskDollar
            ?? riskFallback?.factorRiskDollar
            ?? zeroDollarRisk
        );
        const idioRiskDollar = (
            riskSource?.idioRiskDollar
            ?? riskFallback?.idioRiskDollar
            ?? zeroDollarRisk
        );
        const rawFactorVol = riskSource?.factorVol ?? riskFallback?.factorVol ?? null;
        const rawLinkedIdioVol = riskSource?.idioVol ?? riskFallback?.idioVol ?? null;
        const watchlistPercent = (field) => {
            const value = watchlistFallback ? base?.[field] : null;
            return value == null || !Number.isFinite(Number(value)) ? null : Number(value);
        };
        const linkedIdioVolPct = (
            riskSource?.idio_vol_linked
            ?? riskFallback?.idio_vol_linked
            ?? (rawLinkedIdioVol == null
                ? watchlistPercent('Idio Vol (%)')
                : rawLinkedIdioVol * 100)
        );
        const factorVolPct = rawFactorVol == null
            ? watchlistPercent('Factor Vol (%)')
            : rawFactorVol * 100;

        return {
            ...base,
            id: securityKey,
            beta: viewMode === 'pos-sizing' ? base?.beta ?? null : sel?.beta ?? base?.beta ?? null,
            exp_return: expRet,
            trade_idea: tradeIdea,
            factorRiskDollar,
            idioRiskDollar,
            idio_vol_annual_custom: (
                riskSource?.idio_vol_annual_custom
                ?? riskFallback?.idio_vol_annual_custom
                ?? null
            ),
            idio_vol_linked: linkedIdioVolPct,
            factorVol: factorVolPct,
            positionGmv,
            posSizingPositionGmv,
            // Membership of the chosen baseline: checked unless the user
            // kept a different set for it.
            baselineMember: Boolean(selectedBy[securityKey]),
            checkBoxEnable: keptSelection
                ? keptSelection.has(securityKey)
                : Boolean(selectedBy[securityKey]),
        };
    });
    const rows = preserveOptimizerDisplayIdentity(
        projectedRows,
        industries,
        combinedHoldings,
    );

    rows.sort((a, b) =>
        a.checkBoxEnable === b.checkBoxEnable ? 0 : a.checkBoxEnable ? -1 : 1
    );

    return {
        ready: true, rows, baselineIdentityError, discardKeptSelection, actualMembershipSignature,
        finalTickerDataFlag: selectedBaselineEmpty || (industriesArray.length > 0 && selectedArray.length > 0),
        rowsSignature: buildRowsSignature(rows, `live:${viewMode}:${viewMode === 'pos-sizing'
            ? posSizingOptimizerContext?.context_document_sha256 ?? '' : ''}`),
    };
}

export function useViewModeBaseAndMetrics({ activeView, viewMode, industries, combinedHoldings,
    optimizerWorkspaceAvailable = true, posSizingData, posSizingOptimizerContext, optimizerSelection = null, clearOptimizerSelection,
    setBaseRows, setErrorOptimization, setViewModeLoading, setFinalTickerDataFlag }) {
    const lastBaseRowsSignatureRef = useRef(null);
    const lastBaselineIdentityErrorRef = useRef(null);
    const lastActualMembershipSignatureRef = useRef(null);
    useLayoutEffect(() => {
        if (activeView !== 'optimizer') { setViewModeLoading?.(false); return; }
        const projection = projectOptimizerBaseRows({ viewMode, industries, combinedHoldings, posSizingData,
            optimizerWorkspaceAvailable, posSizingOptimizerContext, optimizerSelection,
            previousActualMembershipSignature: lastActualMembershipSignatureRef.current });
        if (!projection.ready) { setFinalTickerDataFlag(false); setViewModeLoading?.(true); return; }
        const { rows, baselineIdentityError } = projection;
        if (baselineIdentityError) {
            lastBaselineIdentityErrorRef.current = baselineIdentityError;
            setErrorOptimization?.(baselineIdentityError);
        } else if (lastBaselineIdentityErrorRef.current) {
            const prior = lastBaselineIdentityErrorRef.current;
            lastBaselineIdentityErrorRef.current = null;
            setErrorOptimization?.(current => current === prior ? null : current);
        }
        if (projection.discardKeptSelection) clearOptimizerSelection?.('normal');
        setFinalTickerDataFlag(projection.finalTickerDataFlag);
        if (lastBaseRowsSignatureRef.current !== projection.rowsSignature) {
            lastBaseRowsSignatureRef.current = projection.rowsSignature;
            setBaseRows(rows);
        }
        if (viewMode === 'normal') lastActualMembershipSignatureRef.current = projection.actualMembershipSignature;
        setViewModeLoading?.(false);
    }, [activeView, viewMode, industries, combinedHoldings, optimizerWorkspaceAvailable, posSizingData, posSizingOptimizerContext,
        optimizerSelection, clearOptimizerSelection, setBaseRows, setErrorOptimization,
        setFinalTickerDataFlag, setViewModeLoading]);
}
